import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { UnidataTable } from '~/components';
import { MODULO_20_GRAUS, LOCALIDADE_ABASTECIMENTO } from '~/constants/modules';
import { t, can } from '~/helpers';
import { handleExportGeneric } from '~/helpers/exportReports';
import {
  deleteComboio,
  findComboio,
  recordFiltering,
} from '~/store/modules/comboio/actions';

import Form from './form';

function ComboioList() {
  const redux = useSelector((state) => state.comboio);

  const status = useMemo(
    () => [
      { label: 'L', value: t('s.geral.opcoes.liberado') },
      { label: 'B', value: t('s.geral.opcoes.bloqueado') },
      { label: 'C', value: t('s.geral.opcoes.cancelado') },
    ],
    []
  );

  const statusSimNao = useMemo(
    () => [
      { label: 0, value: t('s.geral.opcoes.nao') },
      { label: 1, value: t('s.geral.opcoes.sim') },
    ],
    []
  );

  const header = useMemo(() => {
    const data = [
      {
        id: 'codigoposto',
        label: t('s.configuracao.comboio.idposto'),
        filter: {
          type: 'text',
        },
      },
      {
        id: 'nomeposto',
        label: t('s.configuracao.comboio.nomeposto'),
        filter: {
          type: 'text',
        },
      },
      {
        id: 'codigoauxiliarposto',
        label: t('s.configuracao.comboio.codigoauxiliarposto'),
        filter: {
          type: 'text',
        },
      },
      {
        id: 'nomeabreviadoposto',
        label: t('s.configuracao.comboio.nomeabreviadoposto'),
        filter: {
          type: 'text',
        },
      },
      {
        id: 'codigonomepostoproprietario',
        label: t('s.cadastro.veiculo.postoproprietario'),
        filter: {
          type: 'text',
        },
      },
      {
        id: 'idveiculo',
        label: t('s.configuracao.comboio.idveiculo'),
        filter: {
          type: 'text',
        },
      },
      {
        id: 'statusposto',
        label: t('s.configuracao.comboio.statusposto'),
        formatted: (value) => status.find((s) => s.label === value).value || '',
        filter: {
          type: 'select',
          options: status,
        },
      },
    ];

    if (can(MODULO_20_GRAUS)) {
      data.splice(6, 0, {
        id: 'controla20grausposto',
        label: t('s.configuracao.posto.controla20graus'),
        formatted: (value) =>
          statusSimNao.find((s) => s.label === value).value || '',
      });
    }

    if (can(MODULO_20_GRAUS)) {
      data.splice(7, 0, {
        id: 'operabastecimento20posto',
        label: t('s.configuracao.posto.operabastecimento20'),
        formatted: (value) =>
          statusSimNao.find((s) => s.label === value).value || '',
      });
    }

    if (can(LOCALIDADE_ABASTECIMENTO)) {
      data.splice(6, 0, {
        id: 'nomelocalidadeposto',
        label: t('s.configuracao.posto.localidade'),
        formatted: (value) => value || '',
        filter: {
          type: 'text',
        },
      });
    }

    return data;
  }, [status]);

  const fieldlist = [
    'codigoposto',
    'nomeposto',
    'codigoauxiliarposto',
    'nomeabreviadoposto',
    'codigonomepostoproprietario',
    'idveiculo',
  ];

  if (can(MODULO_20_GRAUS)) {
    fieldlist.push('controla20grausposto', 'operabastecimento20posto');
  }

  if (can(LOCALIDADE_ABASTECIMENTO)) {
    fieldlist.push('nomelocalidadeposto');
  }

  fieldlist.push(
    'tipofechamentoviagemcomboio',
    'centroposto',
    'depositoposto',
    'cnpjposto',
    'leituracontinuattagcomboio',
    'possuittagcomboio',
    'ajustehoraposto',
    'statusposto'
  );

  const substituirHeader = {
    codigoposto: t('s.configuracao.posto.idposto'),
    nomeposto: t('s.configuracao.posto.nomeposto'),
    codigoauxiliarposto: t('s.configuracao.posto.codigoauxiliarposto'),
    nomeabreviadoposto: t('s.configuracao.posto.nomeabreviadoposto'),
    statusposto: t('s.configuracao.posto.statusposto'),
    codigonomepostoproprietario: t('s.cadastro.veiculo.postoproprietario'),
    idveiculo: t('s.configuracao.comboio.idveiculo'),
    // Campos extras
    cnpjposto: t('s.configuracao.posto.cnpjposto'),
    ajustehoraposto: t('s.configuracao.posto.ajustehoraposto'),
    tipofechamentoviagemcomboio: t(
      's.configuracao.comboio.tipofechamentoviagemcomboio'
    ),
    idpostoproprietario: t('s.cadastro.veiculo.postoproprietario'),
    centroposto: t('s.geral.camposistema.centroposto'),
    depositoposto: t('s.geral.camposistema.depositoposto'),
    leituracontinuattagcomboio: t(
      's.configuracao.comboio.leituracontinuattagcomboio'
    ),
    possuittagcomboio: t('s.configuracao.comboio.possuittagcomboio'),
    nomelocalidadeposto: t('s.configuracao.posto.localidade'),
  };

  if (can(MODULO_20_GRAUS)) {
    substituirHeader.controla20grausposto = t(
      's.configuracao.posto.controla20graus'
    );
    substituirHeader.operabastecimento20posto = t(
      's.configuracao.posto.operabastecimento20'
    );
  }

  return (
    <UnidataTable
      fixed
      header={header}
      redux={redux}
      read={recordFiltering}
      find={findComboio}
      remove={deleteComboio}
      form={Form}
      keys="idposto"
      detach="nomeposto"
      pdf
      excel
      onExport={(formato) =>
        handleExportGeneric({
          endpoint: 'comboios',
          formato,
          fields: fieldlist,
          headers: substituirHeader,
          redux,
          ordination: redux.params.ordination,
          orderfield: redux.params.orderfield,
          title: 'Relatório de Comboios',
          substituirValores: {
            tipofechamentoviagemcomboio: {
              '2': t('s.configuracao.terminal.telemetriaterminal'),
              '3': t('s.menu.configuracao.arqueacao'),
              '4': t('s.menu.relatorio.complementoseta'),
            },
            controla20grausposto: {
              '0': t('s.geral.opcoes.nao'),
              '1': t('s.geral.opcoes.sim'),
            },
            operabastecimento20posto: {
              '0': t('s.geral.opcoes.nao'),
              '1': t('s.geral.opcoes.sim'),
            },
            opercompartimentosposto: {
              '0': t('s.geral.opcoes.nao'),
              '1': t('s.geral.opcoes.sim'),
            },
            operrecebimento20posto: {
              '0': t('s.geral.opcoes.nao'),
              '1': t('s.geral.opcoes.sim'),
            },
            leituracontinuattagcomboio: {
              N: t('s.geral.opcoes.nao'),
              S: t('s.geral.opcoes.sim'),
            },
            possuittagcomboio: {
              N: t('s.geral.opcoes.nao'),
              S: t('s.geral.opcoes.sim'),
            },
            statusposto: {
              L: t('s.geral.opcoes.liberado'),
              B: t('s.geral.opcoes.bloqueado'),
              C: t('s.geral.opcoes.cancelado'),
            },
          },
        })
      }
    />
  );
}

export default ComboioList;
